import { Box, Button, Grid, Link, Typography } from '@mui/material';
import { fetchTransactions } from 'apiRequest/transaction';
import SelectInput from 'components/forms/fields/SelectInput';
import ISTable from 'components/lSTable';
import useToken from 'hooks/useToken';
import PrivateLayout from 'layouts/private';
import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Link as RouterLink } from 'react-router-dom';
import dateTime from 'utils/datetime';
import TableButtonsContainer from 'components/TableButtonsContainer';
import TextInput from 'components/forms/fields/TextInput';
import currency from 'utils/currency';
import { fetchRemainingBalances } from 'apiRequest/remainingBalance';
import { fetchAccountsAll } from 'apiRequest/account';

export default function TransactionList() {
  const { token } = useToken();

  const [params, setParams] = useState({
    partials: ['subTransactions'],
    sort_by: {
      "key": "id",
      "order": "desc"
    }
  });

  const { isLoading, isError, data: transactions, error } = useQuery(
    ["transaction-fetchTransactions", params],
    () => {
      return fetchTransactions({ token, params });
    }, {
  });

  const { data: remainingBalances } = useQuery(
    ["transaction-fetchRemainingBalances", params],
    () => {
      return fetchRemainingBalances({ token, params });
    }, {
  });

  const { data: accounts } = useQuery(
    ["account-fetchAccountsAll"],
    () => fetchAccountsAll({ token }),
    {},
  );

  const accountsOptions = useMemo(() => (
    accounts?.data.data.map(({ id, name }) => ({ value: id, label: name }))
  ), [accounts]);

  const handlePageChange = (page) => {
    setParams({ ...params, page });
  };

  const handleSearchSubmit = (search) => {
    setParams({
      ...params,
      filters: { ...params.filters, search }
    });
  };

  const hanldeFilterSubmit = (data) => {
    setParams({
      ...params,
      filters: {
        ...params.filters,
        ...data,
        created_at_from: data.created_at_from ? dateTime.format(data.created_at_from, {
          type: 'ISO',
        }) : undefined,
        created_at_to: data.created_at_to ? dateTime.format(data.created_at_to, {
          type: 'ISO',
          endOfDay: true,
        }) : undefined,
      },
    });
  };

  const hanldeSortClick = (data) => {
    setParams({ ...params, sort_by: data });
  };

  const columns = React.useMemo(() => [
    {
      key: 'id',
      label: 'ID',
      selector: row => <Link component={RouterLink} to={`/transactions/${row.id}`}>{row.id}</Link>,
      sort: true,
    },
    {
      key: 'debtor',
      label: 'Debtor',
      selector: row => row.debtor ? row.debtor : "-",
    },
    {
      key: 'description',
      label: 'Description',
      selector: row => row.description,
    },
    {
      key: 'total_bought_amount',
      label: 'Bought Amount',
      selector: row => `${currency.format(row.total_bought_amount, 2)} ${row.bought_currency}`,
    },
    {
      key: 'total_sold_amount',
      label: 'Sold Amount',
      selector: row => `${currency.format(row.total_sold_amount, 2)} ${row.sold_currency}`,
    },
    {
      key: 'exchange_rate',
      label: 'Exchange Rate',
      selector: row => currency.calculateExchnageRate({
        boughtCurrency: row.bought_currency,
        soldCurrency: row.sold_currency,
        boughtAmount: row.total_bought_amount,
        soldAmount: row.total_sold_amount,
      }),
    },
    {
      key: 'created_at',
      label: 'Created At',
      selector: row => dateTime.format(row.created_at),
      sort: true
    },
  ], []);

  const debtorOptions = ['shop', 'customer', 'settle'].map((v) => ({ value: v, label: v }));
  // dummy total amount data

  return (
    <PrivateLayout loading={isLoading} isError={isError} error={error}>
      <TableButtonsContainer>
        <Button
          variant="contained"
          component={RouterLink}
          to={`/transactions/create`}
        >
          Add New Transaction
        </Button>
      </TableButtonsContainer>
      <ISTable
        title="Transactions"
        loading={isLoading}
        columns={columns}
        data={transactions?.data.data}
        pagination
        count={transactions?.data.total}
        page={transactions?.data.current_page}
        rowsPerPage={transactions?.data.per_page}
        onPageChange={handlePageChange}
        search
        searchFields={['description']}
        searchSubmit={handleSearchSubmit}
        filter
        filterSubmit={hanldeFilterSubmit}
        FilterFields={FilterFields}
        filterFields={['debtor', 'created_at_from', 'created_at_to']}
        filterFieldsProps={{ debtorOptions, accountsOptions }}
        sortClick={hanldeSortClick}
        TableLeftFooter={TableLeftFooter}
        tableLeftFooterProps={{ remainingBalances: remainingBalances?.data?.data }}
      />
    </PrivateLayout>
  );
}

const FilterFields = ({ debtorOptions, accountsOptions }) => {
  return (
    <Grid container direction="row" justify="flex-start" alignItems="center" spacing={2} width="350px" >
      <Grid item xs={6}>
        <SelectInput field="account_id" label="Account" size="small" margin="none" helperText={false}
          options={accountsOptions}
        />
      </Grid>
      <Grid item xs={6}>
        <SelectInput field="debtor" label="Debtor" size="small" margin="none" helperText={false}
          options={debtorOptions}
        />
      </Grid>
      <Grid item xs={6}>
        <TextInput type="date" field="created_at_from" size="small" margin="none" helperText={false} label="Date From" />
      </Grid>
      <Grid item xs={6}>
        <TextInput type="date" field="created_at_to" size="small" margin="none" helperText={false} label="Date To" />
      </Grid>
    </Grid>
  );
};

const TableLeftFooter = ({ remainingBalances }) => {
  return (
    <Box>
      <Typography variant="h6">Total Amount</Typography>
      {remainingBalances?.map((item, index) => (
        <Box display='flex' key={index}>
          <Typography variant="body1" width='50px' fontWeight='bold'>{item.currency}:</Typography>
          <Typography variant="body1">{currency.format(item.remaining_balance, 2)}</Typography>
        </Box>
      ))}
    </Box>
  );
};
